import styled from 'styled-components'

import { Layout } from '@loadsmart/miranda-react'

/**
 * The ask panel needs room for a four-column result table, so this page runs
 * wider than the report library — but still bounded, so the answer sentence
 * inside the panel stays a readable line length.
 */
const CONTENT_MAX_WIDTH = '1080px'

export const StyledContentColumn = styled(Layout.Stack)`
  width: 100%;
  max-width: ${CONTENT_MAX_WIDTH};
  margin: 0 auto;
`

export const StyledSection = styled(Layout.Stack)`
  width: 100%;
`

/**
 * The eyebrow says where the product actually is. It leads the page because a
 * visitor deciding whether to give us their phone number deserves to know that
 * before they read the pitch, not after.
 */
export const StyledEyebrow = styled.div`
  display: inline-flex;
  /* Sits in a stretching column, so it has to opt out of the full width. */
  align-self: flex-start;
  align-items: center;
  gap: var(--m-spacing-2);
  padding: var(--m-spacing-1) var(--m-spacing-3);
  border-radius: 999px;
  font-family: var(--m-font-family-default);
  font-size: var(--m-font-size-2);
  font-weight: var(--m-font-weight-medium);
  color: rgb(var(--m-color-primary-100));
  background-color: rgb(var(--m-color-background-highlight));
`

export const StyledHeadline = styled.h1`
  margin: 0;
  max-width: 26ch;
  font-family: var(--m-font-family-title);
  font-size: var(--m-font-size-8);
  font-weight: var(--m-font-weight-bold);
  line-height: 112%;
  letter-spacing: -0.02em;
  color: rgb(var(--m-color-primary-100));

  @media screen and (max-width: 720px) {
    font-size: var(--m-font-size-7);
  }
`

/**
 * The second half of the headline, in the same size but lighter — the claim is
 * one thought, and breaking it across two weights keeps the emphasis on the
 * part that is new without shouting the whole line.
 */
export const StyledHeadlineSoft = styled.span`
  color: rgb(var(--m-color-neutral-60));
`

export const StyledLede = styled.p`
  margin: 0;
  max-width: 62ch;
  font-family: var(--m-font-family-default);
  font-size: var(--m-font-size-4);
  line-height: 160%;
  color: rgb(var(--m-color-text-tertiary));
`

export const StyledActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--m-spacing-3);
`

/**
 * Captions the panel it sits under. Kept to the shortest true statement — the
 * panel's own badge and the hero's eyebrow already carry the rest.
 */
export const StyledDisclaimer = styled.p`
  display: flex;
  align-items: center;
  gap: var(--m-spacing-2);
  margin: 0;
  font-family: var(--m-font-family-default);
  font-size: var(--m-font-size-2);
  line-height: var(--m-line-height-5);
  color: rgb(var(--m-color-text-placeholder));
`

/* --- what you can ask ----------------------------------------------------- */

export const StyledCapabilityGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: var(--m-spacing-4);
`

export const StyledCapability = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--m-spacing-2);
  padding: var(--m-spacing-5);
  background-color: rgb(var(--m-color-background-primary));
  border: var(--m-border-thin) solid rgb(var(--m-color-border));
  border-radius: var(--m-border-radius-m);
`

/**
 * The example question, set as speech rather than as body copy so it reads as
 * something you would type, not something we wrote about the product.
 */
export const StyledCapabilityExample = styled.p`
  margin: auto 0 0;
  padding-top: var(--m-spacing-3);
  font-family: var(--m-font-family-default);
  font-size: var(--m-font-size-3);
  font-style: italic;
  line-height: var(--m-line-height-5);
  color: rgb(var(--m-color-primary-60));
`

/* --- closing -------------------------------------------------------------- */

export const StyledClosingBand = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--m-spacing-5);
  padding: var(--m-spacing-8);
  background-color: rgb(var(--m-color-primary-100));
  border-radius: var(--m-border-radius-l);

  @media screen and (min-width: 900px) {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }
`

export const StyledClosingCopy = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--m-spacing-2);
  max-width: 52ch;
`

export const StyledClosingTitle = styled.h2`
  margin: 0;
  font-family: var(--m-font-family-title);
  font-size: var(--m-font-size-6);
  line-height: var(--m-line-height-5);
  color: rgb(var(--m-color-text-inverted));
`

export const StyledClosingBody = styled.p`
  margin: 0;
  font-family: var(--m-font-family-default);
  font-size: var(--m-font-size-3);
  line-height: var(--m-line-height-6);
  color: rgba(var(--m-color-neutral-0), 0.68);
`

/**
 * The closing call to action. Styled here because Miranda's buttons are drawn
 * for light surfaces and lose their edges on this one.
 */
export const StyledClosingAction = styled.button`
  display: inline-flex;
  align-items: center;
  align-self: flex-start;
  gap: var(--m-spacing-2);
  padding: var(--m-spacing-3) var(--m-spacing-5);
  cursor: pointer;
  white-space: nowrap;
  font-family: var(--m-font-family-default);
  font-size: var(--m-font-size-3);
  font-weight: var(--m-font-weight-medium);
  color: rgb(var(--m-color-primary-100));
  background-color: rgb(var(--m-color-brand));
  border: var(--m-border-thin) solid rgb(var(--m-color-brand));
  border-radius: 999px;
  transition: background-color 0.15s ease;

  &:hover {
    background-color: rgb(var(--m-color-brand-accent-2));
    border-color: rgb(var(--m-color-brand-accent-2));
  }

  &:focus-visible {
    outline: 2px solid rgb(var(--m-color-brand));
    outline-offset: 2px;
  }
`
